<script setup lang="ts">
import type { IGenericSchoolSubject, TCompetenceDomainListItem } from '@/interfaces';
import ChevronRightIcon from '@/assets/icons/ChevronRightIcon.vue';
import InfoIcon from '@/assets/icons/InfoIcon.vue';
import { useMobile } from '@/composables/useMobile';
import TooltipDialog from '@/core/dialogs/TooltipDialog.vue';
import { EPosition } from 'common/enum/misc.enum';
import { computed, ref, toRefs, useTemplateRef } from 'vue';

const props = withDefaults(
  defineProps<{
    theme?: 'primary' | 'secondary';
    schoolSubject: Pick<IGenericSchoolSubject, 'id' | 'name'>;
    availableCompetenceDomains: TCompetenceDomainListItem[];
  }>(),
  {
    theme: 'secondary',
  },
);
const { schoolSubject, availableCompetenceDomains } = toRefs(props);
const { isMobile } = useMobile();

const anchorElement = useTemplateRef<HTMLElement>('anchorElement');
const dialogRef = useTemplateRef<InstanceType<typeof TooltipDialog>>('dialogRef');
const isDialogOpen = ref<boolean>(false);
const uncollapsedCompetenceDomainIds = ref<number[]>([]);

const competenceDomainsForSchoolSubject = computed(() => {
  return availableCompetenceDomains.value.filter((domain) => domain.schoolSubjectId === schoolSubject.value.id);
});

const tooltipPosition = computed<EPosition>(() => {
  return isMobile.value ? EPosition.TOP_CENTER : EPosition.RIGHT;
});

const tooltipFlipStrategy = computed<'horizontal' | 'vertical'>(() => {
  return isMobile.value ? 'vertical' : 'horizontal';
});

const toggleCompetenceDomainCollapsed = (domain: TCompetenceDomainListItem): void => {
  if (uncollapsedCompetenceDomainIds.value.includes(domain.id)) {
    uncollapsedCompetenceDomainIds.value = uncollapsedCompetenceDomainIds.value.filter((d) => d !== domain.id);
  } else {
    uncollapsedCompetenceDomainIds.value.push(domain.id);
  }
};

const isCompetenceDomainUnCollapsed = (domain: TCompetenceDomainListItem): boolean => {
  return uncollapsedCompetenceDomainIds.value.includes(domain.id);
};

const handleShowDialog = (): void => {
  isDialogOpen.value = true;
  dialogRef.value?.show();
};
</script>

<template>
  <div v-if="competenceDomainsForSchoolSubject.length">
    <button
      ref="anchorElement"
      :class="[
        'flex items-center justify-center',
        'rounded-full h-icon w-icon',
        'transition duration-300',
        'active:text-tint focus-visible:outline-focus',
        theme === 'secondary' && 'bg-surface-secondary hover:bg-surface-tertiary text-secondary',
        theme === 'primary' && 'text-tint hover:text-tint-hover',
        isDialogOpen && 'text-tint',
      ]"
      type="button"
      @click.stop="handleShowDialog"
    >
      <InfoIcon class="shrink-0 w-icon-xs h-icon-xs" />
    </button>
    <TooltipDialog
      v-if="anchorElement"
      ref="dialogRef"
      :anchor-element="anchorElement"
      :position="tooltipPosition"
      :flip-strategy="tooltipFlipStrategy"
      :content-style="'max-h-[500px] w-[360px] overflow-hidden !p-0'"
      @hide="isDialogOpen = false"
    >
      <template #content>
        <div class="size-full overflow-hidden rounded-group">
          <div class="max-h-[500px] overflow-y-auto p-lg">
            <div class="text-title mb-md whitespace-pre-wrap">{{ schoolSubject.name }}</div>
            <ul class="flex flex-col gap-sm">
              <li v-for="domain of competenceDomainsForSchoolSubject" :key="domain.id">
                <button
                  class="w-full flex items-center gap-xs outline-inner"
                  type="button"
                  @click.stop="toggleCompetenceDomainCollapsed(domain)"
                >
                  <ChevronRightIcon
                    class="w-icon-sm h-icon-sm shrink-0"
                    :class="{ 'rotate-90': isCompetenceDomainUnCollapsed(domain) }"
                  />
                  <span class="text-left whitespace-pre-wrap">{{ domain.name }}</span>
                </button>
                <ul v-if="isCompetenceDomainUnCollapsed(domain)" class="flex flex-col gap-sm pl-12 py-sm">
                  <li
                    v-for="competence of domain.competences"
                    :key="competence.id"
                    class="list-disc whitespace-pre-wrap"
                  >
                    {{ competence.description }}
                  </li>
                </ul>
              </li>
            </ul>
          </div>
        </div>
      </template>
    </TooltipDialog>
  </div>
</template>
